import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { StatTile } from '@/components/charts/StatTile'
import { CaptureList } from '@/components/jobs/CaptureList'
import { JobList } from '@/components/jobs/JobList'
import { NewJobPanel } from '@/components/jobs/NewJobPanel'
import { PageHeader } from '@/components/layout/PageHeader'
import { Skeleton } from '@/components/ui/Spinner'
import { useCaptures, useCapturePage, useJobs, isJobActive } from '@/hooks/useJobs'
import { formatNumber, formatRelative } from '@/lib/format'

/** "1 failed run", "7 failed runs", or nothing at all. */
function count(value: number, noun: string): string | null {
  if (value === 0) return null
  return `${formatNumber(value)} ${noun}${value === 1 ? '' : 's'}`
}

/**
 * Bringing records in.
 *
 * The page answers four questions in the order they get asked: what is
 * happening right now, what needs attention, what can I do, and what happened
 * before. The strip at the top covers the first two, which used to be
 * discoverable only by reading the whole job list.
 */
export function IngestPage() {
  const jobs = useJobs()
  // Two reads of the same data, deliberately. The overview tiles count every
  // capture, so they need the unpaged summary; the list below shows one page at
  // a time, because it grows once per search and once per *part* of a split
  // search. Counting only the visible page would make the tiles wrong.
  const captures = useCaptures()
  const [capturesPage, setCapturesPage] = useState(1)
  const [capturesPageSize, setCapturesPageSize] = useState(25)
  const capturePage = useCapturePage(capturesPage, capturesPageSize)
  const navigate = useNavigate()

  const overview = useMemo(() => {
    const jobList = jobs.data ?? []
    const captureList = captures.data ?? []

    const active = jobList.filter(isJobActive)
    const failed = jobList.filter((job) => job.status === 'failed')
    // Captures that actually left records behind. A capped capture that read
    // every page it was shown is NOT one of them: the cap is the portal
    // refusing to display more, not this run stopping short, and counting it
    // here put finished searches in the "needs attention" total for ever.
    const shortCaptures = captureList.filter((capture) => !capture.isComplete)
    const finished = jobList.find((job) => !isJobActive(job))

    // Reported on its own, because it has its own fix — split the search —
    // and it is worth surfacing even though it is not a fault.
    const capped = captureList.filter((capture) => capture.isCapped).length

    return {
      active,
      attention: failed.length + shortCaptures.length,
      capped,
      unfinished: shortCaptures.length,
      failed: failed.length,
      created: jobList.reduce((total, job) => total + job.recordsCreated, 0),
      captureCount: captureList.length,
      lastFinished: finished?.createdAt ?? null,
    }
  }, [jobs.data, captures.data])

  const loading = jobs.isLoading || captures.isLoading

  return (
    <>
      <PageHeader
        title="Ingest"
        description="Bring judgment records into the shared database from a file, a captured session, or a live search of the court portal."
      />

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-3">
          <StatTile
            label="Running now"
            value={overview.active.length > 0 ? formatNumber(overview.active.length) : 'Idle'}
            tone={overview.active.length > 0 ? 'default' : 'default'}
            hint={
              overview.active.length > 0
                ? overview.active.map((job) => job.label).join(', ')
                : overview.lastFinished
                  ? `Last run ${formatRelative(overview.lastFinished)}`
                  : 'Nothing has run yet.'
            }
          />
          <StatTile
            label="Needs attention"
            value={formatNumber(overview.attention)}
            tone={overview.attention > 0 ? 'warning' : 'default'}
            hint={
              overview.attention === 0
                ? 'No failures, nothing truncated.'
                : [
                    count(overview.failed, 'failed run'),
                    count(overview.capped, 'capped capture'),
                    count(overview.unfinished, 'unfinished capture'),
                  ]
                    .filter(Boolean)
                    .join(' · ')
            }
          />
          <StatTile
            label="Records created"
            value={formatNumber(overview.created)}
            hint={`Across ${formatNumber(overview.captureCount)} capture${overview.captureCount === 1 ? '' : 's'}. Duplicates merge rather than repeat.`}
          />
        </div>
      )}

      <div className="flex flex-col gap-5 xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,25rem)]">
        <div className="flex min-w-0 flex-col gap-5">
          <NewJobPanel />
          <CaptureList
            captures={capturePage.data?.items ?? []}
            meta={capturePage.data?.meta}
            onPageChange={setCapturesPage}
            onPageSizeChange={(size) => {
              setCapturesPageSize(size)
              // Back to the first page: page 7 of a 20-per-page list is not
              // page 7 of a 100-per-page one, and staying put can land past
              // the end and show nothing.
              setCapturesPage(1)
            }}
            onFilterByCapture={(captureId) => navigate(`/?captureId=${captureId}`)}
          />
        </div>

        <div className="min-w-0">
          <div className="xl:sticky xl:top-18">
            <JobList jobs={jobs.data ?? []} loading={jobs.isLoading} />
          </div>
        </div>
      </div>
    </>
  )
}
